import { Avatar, Divider, Layout, Menu } from "@shared/ui";
import { DownOutlined, LeftOutlined, LogoutOutlined, RightOutlined, UpOutlined, User } from "@shared/ui/icons";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ROUTES } from "@app/constants";
import { useAuth } from "@entities/auth";
import { MenuItemGroupType, MenuItemType } from "antd/es/menu/interface";
import { menuItems } from "../constants/menuItems";
import styles from "./sidebar.module.scss";

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
  toggleSidebar: () => void;
}

const getActiveMenuKey = (pathname: string): string[] => {
    if (pathname === ROUTES.MY_PRICING || pathname.startsWith(`${ROUTES.MY_PRICING}/`)) {
      return [ROUTES.MY_PRICING];
    }
    if (pathname === ROUTES.BRIEF || pathname.startsWith(`${ROUTES.BRIEF}/`)) {
      return [ROUTES.BRIEF];
    }
    if (pathname === ROUTES.CLOSURES || pathname.startsWith(`${ROUTES.CLOSURES}/`)) {
      return [ROUTES.CLOSURES];
    }
    if (pathname === ROUTES.REPORTS || pathname.startsWith(`${ROUTES.REPORTS}/`)) {
      return [ROUTES.REPORTS];
    }
    if (pathname === ROUTES.BUCKET || pathname.startsWith(`${ROUTES.BUCKET}/`)) {
      return [ROUTES.BUCKET];
    }
    return [pathname];
  };

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  toggleSidebar,
}) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { user, beUser } = useAuth();
  const isSuperAdmin = beUser?.permissions?.includes("super");

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSignOut = () => {
    logout();
    navigate(ROUTES.LOGIN);
    setIsDropdownOpen(false);
  };

  const isFinanceEnabled = beUser?.permissions?.includes(
    "view-finance-modules"
  );
  const isAnalyticsEnabled = beUser?.permissions?.includes(
    "view-analytics-modules"
  );
  const isAgencyEnabled = beUser?.permissions?.includes(
    "view-agency-management-modules"
  );

  const isPermissionGroupsEnabled = beUser?.permissions?.includes(
    "view-permission-module"
  );

  const meuItemsUpdated = isSuperAdmin
    ? menuItems
    : menuItems.filter((item) => {
        if (String(item?.key) === "analytics" && !isAnalyticsEnabled)
          return false;
        if (String(item?.key) === "finance" && !isFinanceEnabled) return false;
        return true;
      });

  const menuItemsWithAgencyFilter =
    isAgencyEnabled || isSuperAdmin
      ? meuItemsUpdated
      : meuItemsUpdated.map((item) => {
          if (item && String(item.key) === "agency" && item.type === "group") {
            const groupItem = item as MenuItemGroupType<MenuItemType>;
            return {
              ...groupItem,
              children: groupItem.children?.filter(
                (child: any) => child?.key !== ROUTES.REQUESTS
              ),
            };
          }
          return item;
        });

  const menuItemsWithPermissionGroupsFilter =
    isPermissionGroupsEnabled || isSuperAdmin
      ? menuItemsWithAgencyFilter
      : menuItemsWithAgencyFilter.map((item) => {
          if (item && String(item.key) === "admin" && item.type === "group") {
            const groupItem = item as MenuItemGroupType<MenuItemType>;
            return {
              ...groupItem,
              children: groupItem.children?.filter(
                (child: any) => child?.key !== ROUTES.PERMISSIONS
              ),
            };
          }
          return item;
        });

  const canAccessYoutubeSearch =
    isSuperAdmin || beUser?.permissions?.includes("allocate-planning");

  const visibleMenuItems = canAccessYoutubeSearch
    ? menuItemsWithPermissionGroupsFilter
    : menuItemsWithPermissionGroupsFilter.map((item) => {
        if (item && String(item.key) === "influencer" && item.type === "group") {
          const groupItem = item as MenuItemGroupType<MenuItemType>;
          return {
            ...groupItem,
            children: groupItem.children?.filter(
              (child) => child && "key" in child && child.key !== ROUTES.ADVANCED_YOUTUBE_SEARCH
            ),
          };
        }
        return item;
      });

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={toggleSidebar}
      className={styles.sidebar}
      width={224}
      collapsedWidth={64}
      theme="light"
      trigger={null}
    >
      <div className={styles.toggleContainer} onClick={toggleSidebar}>
        <div className={styles.toggleIndicator} />
        <div className={styles.toggleButton}>
          {collapsed ? <RightOutlined style={{ fontSize: 12 }} /> : <LeftOutlined style={{ fontSize: 12 }} />}
        </div>
      </div>

      <div className={`${styles.content} ${collapsed ? styles.overflowVisible : ""}`}>
        <div className={styles.mainNav}>
          <div className={styles.brandSection}>
            <div
              ref={triggerRef}
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`${styles.brandTrigger} ${
                isDropdownOpen ? styles.active : ""
              } ${collapsed ? styles.collapsed : ""}`}
            >
              <Avatar
                src="/cloutflow.svg"
                size={24}
                shape="square"
                style={{ minWidth: "24px" }}
              />

              {!collapsed && (
                <span className={styles.brandName}>
                  Cloutflow
                </span>
              )}
              {!collapsed &&
                (isDropdownOpen ? (
                  <UpOutlined style={{ fontSize: 10, color: "#a3acb9" }} />
                ) : (
                  <DownOutlined style={{ fontSize: 10, color: "#a3acb9" }} />
                ))}
            </div>

            {isDropdownOpen && (
              <div
                ref={dropdownRef}
                className={`${styles.brandDropdown} ${
                  collapsed ? styles.collapsedDropdown : ""
                }`}
              >
                <div className={styles.brandDropdownHeader}>
                  <Avatar
                    src="/cloutflow.svg"
                    size={34}
                    shape="square"
                  />
                  <h4>Admin User</h4>
                </div>
                <div className={styles.brandDropdownDivider} />
                <div className={styles.brandDropdownItem} onClick={handleSignOut}>
                  <LogoutOutlined style={{ color: "#a3acb9" }} />
                  <span>Sign out</span>
                </div>
              </div>
            )}
          </div>

          <Menu
            theme="light"
            mode="inline"
            inlineCollapsed={collapsed}
            selectedKeys={getActiveMenuKey(location.pathname)}
            defaultOpenKeys={["admin", "brand", "influencer"]}
            onClick={({ key }) => navigate(key)}
            items={visibleMenuItems}
            style={{ overflowY: "auto", flex: 1 }}
          />
        </div>

        <div className={styles.footerSection}>
          <Divider style={{ margin: "4px 0" }} />
          <Menu
            theme="light"
            mode="inline"
            inlineCollapsed={collapsed}
            selectable={false}
            onClick={({ key }) => navigate(key)}
            items={[
              {
                icon: <User />,
                label: beUser ? `${beUser.firstName} ${beUser.lastName}`.trim() : (user?.displayName || "Profile"),
                key: ROUTES.PERSONAL_SETTINGS,
              },
            ]}
          />
        </div>
      </div>
    </Sider>
  );
};
